"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getOptionPnlForDomain = void 0;
const types_1 = require("../types");
const blackScholes_1 = require("./blackScholes");
const calculationUtils_1 = require("./calculationUtils");
const volatilitySmile_1 = require("./volatilitySmile");
const IMPLIED_VOL_MIN_TEXP = 0.000114155;
// ----------------------------------------- //
// ---------------- PUBLIC ----------------- //
// ----------------------------------------- //
/**
 * Calculates pnl for an option over a domain.
 *
 * @param {number[]} domain - Array of values that represent the x axis for calculation.
 * @param currencyType
 * @param {number} r - The interest rate
 * @param {number} t - Time to expiry
 * @param {Position} position - Position that we calculate the pnl for
 * @param {number} strike - The strike of the position
 * @param {Tickers} tickers - Additional values needed for the calculation. For example current index price, underlying price etc.
 * @param {OptionType} optionType - Type of the option
 * @param implVolType
 * @param {ImpliedVolatilityType} optionType - Type of the volatility. Stiky strike or delta.
 * @returns {{todayLine: number[],expireLine: number[] }}
 */
const getOptionPnlForDomain = (domain, currencyType, r, t, position, strike, tickers, optionType, implVolType, isOffsetActive) => {
    let todayLine = [];
    let expireLine = [];
    const volatilitySmile = (0, volatilitySmile_1.calculateVolatilitySmileForDomain)(domain, tickers, position, implVolType, t, IMPLIED_VOL_MIN_TEXP);
    for (const i in domain) {
        let amount = position.amount;
        const averagePrice = position.averagePrice;
        const { expirePt, todayPt } = calculateOptionPointsForIndexX(currencyType, domain[i], r, t, strike, volatilitySmile[i], averagePrice, amount, optionType, tickers[position.instrument].markPrice, isOffsetActive);
        todayLine.push(todayPt);
        expireLine.push(expirePt);
    }
    return {
        todayLine,
        expireLine,
        volatilitySmile,
    };
};
exports.getOptionPnlForDomain = getOptionPnlForDomain;
// ----------------------------------------- //
// ---------------- PRIVATE ---------------- //
// ----------------------------------------- //
/**
 * Calculates the pnl for an option position at certain point.
 *
 * @param currencyType
 * @param x
 * @param r
 * @param t
 * @param strike
 * @param volatility
 * @param averagePrice
 * @param amount
 * @param optionType
 * @returns {expirePt, todayPt} Expected pnl for the price x at expiry and today.
 */
const calculateOptionPointsForIndexX = (currencyType, x, r, t, strike, volatility, averagePrice, amount, optionType, markPrice, isOffsetActive) => {
    const Forward = x * Math.exp(r * t);
    const todayPtUSD = (0, blackScholes_1.blackScholes)(optionType, Forward, strike, t, r, volatility);
    const entryPrice = isOffsetActive ? markPrice : averagePrice;
    const todayPtBtc = getOptionTodayCryptoPnl(todayPtUSD, entryPrice, Forward, amount, optionType);
    // TODO: we multiply by forward here because platform does it that way. It is wrong on purpose.
    const todayPt = currencyType === types_1.CurrencyType.FIAT ? todayPtBtc * Forward : todayPtBtc;
    const expirePtBtc = getOptionExpireCryptoPnl(entryPrice, strike, x, amount, optionType);
    const expirePt = currencyType === types_1.CurrencyType.FIAT ? expirePtBtc * Forward : expirePtBtc;
    return {
        expirePt: (0, calculationUtils_1.sanitizeNumber)(expirePt),
        todayPt: (0, calculationUtils_1.sanitizeNumber)(todayPt),
    };
};
/**
 *
 * @param averagePrice
 * @param strike
 * @param x
 * @param amount
 * @param optionType
 * @returns
 */
const getOptionExpireCryptoPnl = (averagePrice, strike, x, amount, optionType) => {
    if (optionType === types_1.OptionType.CALL) {
        return (Math.max(0, (x - strike) / x) - averagePrice) * amount;
    }
    else {
        return (Math.max(0, (strike - x) / x) - averagePrice) * amount;
    }
};
/**
 * Calculates the pnl for an option given the price at expiry is x in crypto currency.
 *
 * @param todayPtUSD
 * @param averagePrice
 * @param x
 * @param amount
 * @param optionType
 * @returns The pnl for the point at expiry in crypto
 */
const getOptionTodayCryptoPnl = (todayPtUSD, averagePrice, forward, amount, optionType) => {
    return (todayPtUSD / forward - averagePrice) * amount;
};
